import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import TabNavigation from '../../components/ui/TabNavigation';
import PrivacyToggle from '../../components/ui/PrivacyToggle';
import QuickActionFloat from '../../components/ui/QuickActionFloat';
import EncounterCard from './components/EncounterCard';
import FeedbackModal from './components/FeedbackModal';
import ProgressAnalytics from './components/ProgressAnalytics';
import FilterBar from './components/FilterBar';

const EncounterManagement = () => {
  const [activeTab, setActiveTab] = useState('encounters');
  const [selectedEncounter, setSelectedEncounter] = useState(null);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [filters, setFilters] = useState({
    status: 'all',
    sortBy: 'date-desc'
  });

  // Mock data removed - ready for live data integration
  const mockEncounters = [];
  const mockAnalytics = {
    totalEncounters: 0,
    completionRate: 0,
    avgSatisfaction: 0,
    fulfillmentScore: 0,
    satisfactionTrends: []
  };

  const handleConfirm = (encounterId) => {
    console.log('Confirming encounter:', encounterId);
  };

  const handleModify = (encounterId) => {
    console.log('Modifying encounter:', encounterId);
  };

  const handleFeedback = (encounterId) => {
    const encounter = mockEncounters?.find((e) => e?.id === encounterId);
    setSelectedEncounter(encounter);
    setIsFeedbackModalOpen(true);
  };

  const handleSubmitFeedback = async (feedbackData) => {
    console.log('Submitting feedback:', feedbackData);
    await new Promise((resolve) => setTimeout(resolve, 1000));
  };

  const handleFilterChange = (filterType, value) => {
    if (filterType === 'reset') {
      setFilters({
        status: 'all',
        sortBy: 'date-desc'
      });
    } else {
      setFilters((prev) => ({
        ...prev,
        [filterType]: value
      }));
    }
  };

  const getFilteredEncounters = () => {
    let filtered = [...mockEncounters];

    if (filters?.status !== 'all') {
      filtered = filtered?.filter((e) => e?.status === filters?.status);
    }

    filtered?.sort((a, b) => {
      switch (filters?.sortBy) {
        case 'date-desc':
          return new Date(b.date) - new Date(a.date);
        case 'date-asc':
          return new Date(a.date) - new Date(b.date);
        case 'satisfaction-desc':
          return b?.moodMatch - a?.moodMatch;
        case 'satisfaction-asc':
          return a?.moodMatch - b?.moodMatch;
        default:
          return 0;
      }
    });

    return filtered;
  };

  const filteredEncounters = getFilteredEncounters();

  return (
    <>
      <Helmet>
        <title>Encounter Management - LoveNudge</title>
        <meta name="description" content="Manage your intimate encounters, provide feedback, and track relationship progress with AI-powered insights." />
      </Helmet>
      <div className="min-h-screen bg-background">
        <TabNavigation />
        <PrivacyToggle />
        <QuickActionFloat />

        <main className="pt-[60px] pb-20 lg:pb-6 px-4 md:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="mb-6 md:mb-8">
              <h1 className="text-3xl md:text-4xl font-heading font-semibold text-foreground mb-2">
                Encounter Management
              </h1>
              <p className="text-base md:text-lg text-muted-foreground">
                Track your intimate journey and celebrate your connection
              </p>
            </div>

            <div className="mb-6">
              <div className="flex items-center space-x-2 border-b border-border">
                <button
                  onClick={() => setActiveTab('encounters')}
                  className={`px-4 py-3 text-sm md:text-base font-medium transition-therapeutic relative ${
                  activeTab === 'encounters' ? 'text-primary' : 'text-muted-foreground hover:text-foreground'}`
                  }>

                  <span>Encounters</span>
                  {activeTab === 'encounters' &&
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
                  }
                </button>
                <button
                  onClick={() => setActiveTab('analytics')}
                  className={`px-4 py-3 text-sm md:text-base font-medium transition-therapeutic relative ${
                  activeTab === 'analytics' ? 'text-primary' : 'text-muted-foreground hover:text-foreground'}`
                  }>

                  <span>Progress & Insights</span>
                  {activeTab === 'analytics' &&
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
                  }
                </button>
              </div>
            </div>

            {activeTab === 'encounters' &&
            <div className="space-y-6">
                <FilterBar filters={filters} onFilterChange={handleFilterChange} />

                {filteredEncounters?.length === 0 ?
              <div className="bg-card border border-border rounded-md p-8 md:p-12 text-center">
                    <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                      <Icon name="Calendar" size={32} color="var(--color-muted-foreground)" />
                    </div>
                    <h3 className="text-lg md:text-xl font-heading font-semibold text-foreground mb-2">
                      No Encounters Found
                    </h3>
                    <p className="text-sm md:text-base text-muted-foreground mb-6 max-w-md mx-auto">
                      No encounters match your current filters. Try adjusting your filter settings or schedule a new encounter.
                    </p>
                    <Button variant="default" iconName="Plus" iconPosition="left">
                      Schedule Encounter
                    </Button>
                  </div> :

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
                    {filteredEncounters?.map((encounter) =>
                <EncounterCard
                  key={encounter?.id}
                  encounter={encounter}
                  onConfirm={handleConfirm}
                  onModify={handleModify}
                  onFeedback={handleFeedback} />

                )}
                  </div>
              }
              </div>
            }

            {activeTab === 'analytics' &&
            <ProgressAnalytics analyticsData={mockAnalytics} />
            }
          </div>
        </main>

        {selectedEncounter &&
        <FeedbackModal
          encounter={selectedEncounter}
          isOpen={isFeedbackModalOpen}
          onClose={() => {
            setIsFeedbackModalOpen(false);
            setSelectedEncounter(null);
          }}
          onSubmit={handleSubmitFeedback} />

        }
      </div>
    </>);

};

export default EncounterManagement;